import React, { useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { addTab, setActiveTab } from "../../../../redux/slices/manageTeamSlice";
import { MoreHorizontal, ChevronRight } from "lucide-react";
import Table from "../../../../components/ACS-Dashboard/ACS-Blocks/Table";
import Loader from "../../../../components/ACS-Dashboard/ACS-Blocks/Loader";
import ActionMenu from "../../../../components/ACS-Dashboard/ACS-Blocks/ActionMenu";
import Cookie from "js-cookie";
import { fetchLocationsByOfficeCodes, fetchChildrenByLocationRef } from "../api";
import { selectModuleTabs } from "../../../../redux/selectors/tabsSelectors";
import GlobalPagination from "../../../../components/ACS-Dashboard/ACS-Blocks/GlobalPagination";

const TABLE_HEADERS = [
  "Selection", "Action", "Location Name", "ERP Identifier",
  "Address Line 1", "Address Line 2", "City", "State", "Zip",
  "Country", "Appointment URL", "Contact Person", "Phone Number",
  "Email", "Map Url",
];

const SORTABLE_COLUMNS = {
    "Location Name": "locationName",
    "ERP Identifier": "customLocationIdentifier",
    "Address Line 1": "addressLine1",
    "Address Line 2": "addressLine2",
    "City": "city",
    "State": "state",
    "Zip": "zipCode",
    "Country": "country",
    "Appointment URL": "appointmentUrl",
    "Contact Person": "contactName",
    "Phone Number": "phoneNumber",
    "Email": "email",
};

// Shared cell style
const tdStyle = { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", width: "200px", minWidth: "200px", maxWidth: "200px" };
const tdBase  = "px-2 sm:px-3 py-1 sm:py-2 text-gray-900 dark:text-acs-dark-text text-xs sm:text-sm md:text-base border-b border-gray-200 dark:border-acs-dark-border transition-none font-roboto";

// Get cell content for a given header from a row object
const getCellContent = (header, row) => {
  switch (header) {
    case "Location Name":    return row.locationName             || "-";
    case "ERP Identifier":   return row.customLocationIdentifier || "-";
    case "Address Line 1":   return row.addressLine1             || "-";
    case "Address Line 2":   return row.addressLine2             || "-";
    case "City":             return row.city                     || "-";
    case "State":            return row.state                    || "-";
    case "Zip":              return row.zipCode                  || "-";
    case "Country":          return row.country                  || "-";
    case "Appointment URL":  return row.appointmentUrl           || "-";
    case "Contact Person":   return row.contactPerson            || "-";
    case "Phone Number":     return row.phoneNumber              || "-";
    case "Email":            return row.email                    || "-";
    case "Map Url":
      return row.latitude && row.longitude ? (
        <a href={`https://www.google.com/maps/?q=${row.latitude},${row.longitude}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 underline">
          View Map
        </a>
      ) : "-";
    default: return "-";
  }
};

const LocationManagement = () => {
  const [locations,         setLocations]         = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [children,          setChildren]          = useState([]);
  const [locationRef,       setLocationRef]       = useState(null);
  const [expandedRows,      setExpandedRows]      = useState(new Set());
  const [isLoading,         setIsLoading]         = useState(false);
  const [openDropdownRow,   setOpenDropdownRow]   = useState(null);
  const [dropdownPos,       setDropdownPos]       = useState({ top: 0, left: 0 });
  const [currentPage,       setCurrentPage]       = useState(1);
  const [locationsPerPage,  setLocationsPerPage]  = useState(10);
  const [showBulkMenu,      setShowBulkMenu]      = useState(false);
  const [rowChildren,       setRowChildren]       = useState({});
  const [sortBy, setSortBy] = useState("locationName");
  const [ascending, setAscending] = useState(true);
  const dropdownRef       = useRef(null);
  const checkboxHeaderRef = useRef(null);
  const { tabs }          = useSelector(selectModuleTabs("manageTeam"));
  const dispatch          = useDispatch();
  const officeCode        = Cookie.get("officeCode");
  const token             = Cookie.get("authToken") || null;

  const isExpanded  = (id) => expandedRows.has(id);
  const toggleExpand = (id) => setExpandedRows((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const refreshLocations = () => {
    fetchLocationsByOfficeCodes().then((res) => {
      const arr    = Array.isArray(res.data) ? res.data : [];
      const sorted = [...arr].sort((a, b) => a.locationName.localeCompare(b.locationName));
      setLocations(sorted);
      setChildren(res.data?.children || []);
    });
  };

  useEffect(() => { refreshLocations(); }, [officeCode, token]);

  // Close portal dropdown on outside click
  useEffect(() => {
    const handle = (e) => {
      if (e.target.closest("[data-action-trigger]")) return;
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setOpenDropdownRow(null);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  const handleActionClick = (id, e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX });
    setOpenDropdownRow((prev) => (prev === id ? null : id));
  };

  const handleAddLocation = () => {
    const id = `add-location-${Date.now()}`;
    dispatch(addTab({ id, name: "Add Location", contentType: "addLocation", isDynamic: true, createdAt: Date.now(), data: { locationRef, token } }));
    dispatch(setActiveTab({ index: tabs.length - 1 }));
  };

  const handleviewLocations = (location) => {
    dispatch(addTab({ id: `view-location-${location.id}-${Date.now()}`, name: `Location: ${location.locationName}`, contentType: "viewLocation", data: location, isDynamic: true, createdAt: Date.now() }));
    dispatch(setActiveTab({ index: tabs.length }));
  };

    const handleSort = (header) => {
        const key = SORTABLE_COLUMNS[header];
        if (!key) return;
        if (sortBy === key) {
            setAscending(a => !a);
        } else {
            setSortBy(key);
            setAscending(true);
        }
    };

    const handleSelectAll      = (checked) => setSelectedLocations(checked ? locations.map((l) => l.id) : []);
  const handleSingleCheckbox = (id)      => setSelectedLocations((prev) => prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id]);

  // Shared portal action button — used for both parent and child rows
  const renderActionButton = (id) => (
    <button
      data-action-trigger
      className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-acs-dark-text w-full"
      onClick={(e) => handleActionClick(id, e)}
    >
      <MoreHorizontal size={18} className="mx-auto" />
    </button>
  );

  const renderActionPortal = (row) =>
    openDropdownRow === row.id && ReactDOM.createPortal(
      <div
        ref={dropdownRef}
        style={{ position: "absolute", top: dropdownPos.top, left: dropdownPos.left, zIndex: 9999 }}
      >
        <ActionMenu
          rowId={row.id}
          isOpen
          onClose={() => setOpenDropdownRow(null)}
          moduleName="Location Management"
          actions={[
            { icon: "Eye",   label: "View",   color: "blue", action: () => handleviewLocations(row) },
            { icon: "Trash", label: "Remove", color: "red",  action: () => console.log("TODO: Remove location") },
          ]}
        />
      </div>,
      document.body
    );

    const allLocations = [...locations, ...children];

    const sortedLocations = !sortBy ? allLocations : [...allLocations].sort((a, b) => {
        const aVal = (a[sortBy] ?? "").toString().toLowerCase();
        const bVal = (b[sortBy] ?? "").toString().toLowerCase();
        return ascending ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    });
    const totalPages         = Math.max(1, Math.ceil(sortedLocations.length / locationsPerPage));
    const paginatedLocations = sortedLocations.slice((currentPage - 1) * locationsPerPage, currentPage * locationsPerPage);
  return (
    <div className="bg-white dark:bg-acs-dark-surface flex flex-col h-full transition-none">
      {isLoading ? (
        <Loader color="text-blue-600 dark:text-acs-dark-text" size="h-5 w-5" message="Loading locations..." />
      ) : (
        <>
          <div className="flex-1 min-h-[200px] sm:min-h-[300px] overflow-x-auto bg-white dark:bg-acs-dark-surface transition-none">
            <Table
              headers={TABLE_HEADERS}
              data={paginatedLocations}
              onSelectAll={handleSelectAll}
              renderCheckboxActions={() => setShowBulkMenu((prev) => !prev)}
              checkboxHeaderRef={checkboxHeaderRef}
              stickyColumns={[0, 1, 2]}
              onSort={handleSort}
              sortableHeaders={Object.keys(SORTABLE_COLUMNS)}
              sortBy={sortBy}
              ascending={ascending}
              sortKeyByHeader={(h) => SORTABLE_COLUMNS[h]}
              renderChildRows={(row) => {
                if (!isExpanded(row.id)) return null;
                const kids = rowChildren[row.id] || [];

                if (kids.length === 0) {
                  return (
                    <tr key={`no-children-${row.id}`}>
                      <td colSpan={TABLE_HEADERS.length} className="px-3 py-2 text-xs text-gray-500 dark:text-acs-dark-subtext border-b border-gray-200 dark:border-acs-dark-border">
                        No child locations found for <b>{row.locationName}</b>
                      </td>
                    </tr>
                  );
                }

                return kids.map((child) => (
                  <tr key={`child-${child.id}`} className="bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/30 [&>td]:py-1.5 [&>td]:text-[11px] sm:[&>td]:text-sm">

                    {/* Child checkbox */}
                    <td
                      className="sticky left-0 bg-purple-50 dark:bg-purple-900/20 border-b border-gray-200 dark:border-acs-dark-border z-[10] transition-none"
                      style={{ width: "80px", minWidth: "80px", maxWidth: "80px", boxShadow: "2px 0 2px -1px rgba(0,0,0,0.08)" }}
                    >
                      <input
                        type="checkbox"
                        className="cursor-pointer mx-auto block"
                        checked={selectedLocations.includes(child.id)}
                        onChange={() => handleSingleCheckbox(child.id)}
                      />
                    </td>

                    {/* Child action */}
                    <td
                      onClick={(e) => e.stopPropagation()}
                      className="sticky left-[80px] bg-purple-50 dark:bg-purple-900/20 border-b border-gray-200 dark:border-acs-dark-border text-center transition-none z-[10]"
                      style={{ width: "50px", minWidth: "50px", maxWidth: "50px", boxShadow: "2px 0 2px -1px rgba(0,0,0,0.08)" }}
                    >
                      {renderActionButton(child.id)}
                    </td>

                    {renderActionPortal(child)}

                    {/* Child sticky "Location Name" */}
                    <td
                    className="sticky px-2 sm:px-3 py-1 sm:py-2 bg-purple-50 dark:bg-purple-900/20 text-gray-900 dark:text-acs-dark-text text-xs sm:text-sm md:text-base border-b border-gray-200 dark:border-acs-dark-border font-roboto transition-none z-[9]"
                    style={{ left: "130px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", width: "300px", minWidth: "300px", maxWidth: "300px", boxShadow: "2px 0 2px -1px rgba(0,0,0,0.08)" }}
                    onDoubleClick={() => handleviewLocations(child)}
                    >
                      <div className="flex items-center gap-2">
                        <span className="inline-block w-3 h-3 border-l-2 border-b-2 border-gray-500 -ml-1" />
                        <span className="font-medium text-gray-700 dark:text-acs-dark-text">{child.locationName}</span>
                      </div>
                    </td>

                    {/* Child data columns */}
                    {TABLE_HEADERS.slice(3).map((header) => (
                      <td
                        key={header}
                        className="px-2 sm:px-3 py-1 text-gray-900 dark:text-acs-dark-text text-xs border-b border-gray-200 dark:border-acs-dark-border font-roboto transition-none"
                        style={tdStyle}
                        onDoubleClick={() => handleviewLocations(child)}
                      >
                        {getCellContent(header, child)}
                      </td>
                    ))}
                  </tr>
                ));
              }}
            >
              {(row) => (
                <>
                  {/* Column 0 — Checkbox */}
                  <td
                    className="sticky left-0 bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border z-[10] transition-none"
                    style={{ width: "80px", minWidth: "80px", maxWidth: "80px", boxShadow: "2px 0 2px -1px rgba(0,0,0,0.1)" }}
                    onDoubleClick={() => handleviewLocations(row)}
                  >
                    <input
                      type="checkbox"
                      className="cursor-pointer mx-auto block"
                      checked={selectedLocations.includes(row.id)}
                      onChange={() => handleSingleCheckbox(row.id)}
                    />
                  </td>

                  {/* Column 1 — Action (portal dropdown) */}
                  <td
                    onClick={(e) => e.stopPropagation()}
                    className="sticky left-[80px] bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border text-center transition-none z-[10]"
                    style={{ width: "50px", minWidth: "50px", maxWidth: "50px", boxShadow: "2px 0 2px -1px rgba(0,0,0,0.1)" }}
                  >
                    {renderActionButton(row.id)}
                  </td>

                  {renderActionPortal(row)}

                  {/* Column 2 — Location Name (sticky, with expand chevron) */}
                  <td
                    className="sticky px-2 sm:px-3 py-1 sm:py-2 bg-white dark:bg-acs-dark-surface text-gray-900 dark:text-acs-dark-text text-xs sm:text-sm md:text-base border-b border-gray-200 dark:border-acs-dark-border font-roboto transition-none z-[9]"
                    style={{ left: "130px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", width: "300px", minWidth: "300px", maxWidth: "300px", boxShadow: "2px 0 2px -1px rgba(0,0,0,0.1)" }}
                    onDoubleClick={() => handleviewLocations(row)}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-gray-800 dark:text-acs-dark-text truncate">{row.locationName}</span>
                      <ChevronRight
                        size={16}
                        className={`text-[#0c1629] dark:text-acs-dark-text shrink-0 transition-transform duration-200 cursor-pointer ${isExpanded(row.id) ? "rotate-90" : "rotate-0"}`}
                        title={isExpanded(row.id) ? "Hide children" : "Show children"}
                        onClick={async (e) => {
                          e.stopPropagation();
                          toggleExpand(row.id);
                          if (!isExpanded(row.id)) {
                            try {
                              const res = await fetchChildrenByLocationRef(row.locationRef);
                              setRowChildren((prev) => ({ ...prev, [row.id]: res.data || [] }));
                            } catch (err) {
                              console.error("Failed to fetch child locations:", err);
                              setRowChildren((prev) => ({ ...prev, [row.id]: [] }));
                            }
                          }
                        }}
                      />
                    </div>
                  </td>

                  {/* Columns 3+ — remaining data */}
                  {TABLE_HEADERS.slice(3).map((header) => (
                    <td
                      key={header}
                      className={tdBase}
                      style={tdStyle}
                      onDoubleClick={() => handleviewLocations(row)}
                    >
                      {getCellContent(header, row)}
                    </td>
                  ))}
                </>
              )}
            </Table>
          </div>

          <button
            className="fixed bottom-8 right-8 z-50 bg-blue-600 hover:bg-blue-700 text-white rounded-full w-14 h-14 flex items-center justify-center shadow-lg transition"
            title="Add Location"
          >
            <svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M12 4v16m8-8H4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          <GlobalPagination
            label="Locations per page"
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={locationsPerPage}
            onItemsPerPageChange={(n) => { setLocationsPerPage(n); setCurrentPage(1); }}
          />
        </>
      )}
    </div>
  );
};

export default LocationManagement;